/**
 * TerrainConstructionSystem — dynamic terrain placement for the Earth tree.
 *
 * Allows the player to place walls, pillars, and formations that have HP,
 * duration, collision, proximity effects, and destruction effects. Pure
 * logic — Phaser physics bodies and pathfinding updates are wired in the
 * integration phase.
 *
 * @see ADR-064 (attunement shared systems architecture)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TerrainType = 'wall' | 'pillar' | 'formation';

export interface ProximityEffect {
  readonly radiusTiles: number;
  readonly damageReduction?: number;
  readonly dotDefId?: string;
  readonly dotIntensity?: number;
}

export interface DestroyEffect {
  readonly explosionDamage?: number;
  readonly explosionRadiusTiles?: number;
  readonly knockbackTiles?: number;
}

export interface TerrainConfig {
  readonly type: TerrainType;
  readonly position: { x: number; y: number };
  readonly widthTiles: number;
  readonly heightTiles: number;
  readonly hp: number;
  /** Duration in ms. 0 = permanent until destroyed. */
  readonly durationMs: number;
  readonly sourceSkillId: string;
  readonly blocksMovement: boolean;
  readonly blocksProjectiles: boolean;
  /** When true, damage() is a no-op — the terrain cannot be destroyed by hits. */
  readonly indestructible?: boolean;
  readonly proximityEffect?: ProximityEffect;
  readonly onDestroyEffect?: DestroyEffect;
}

export interface ActiveTerrain {
  readonly terrainId: string;
  readonly config: TerrainConfig;
  hp: number;
  remainingMs: number;
  readonly isDead: boolean;
}

/** Emitted when terrain is placed, destroyed, or expires. */
export interface TerrainEvent {
  readonly type: 'placed' | 'destroyed' | 'expired' | 'damaged';
  readonly terrainId: string;
  readonly config: TerrainConfig;
  readonly x: number;
  readonly y: number;
  /** Present for 'destroyed' events when onDestroyEffect is configured. */
  readonly destroyEffect?: DestroyEffect;
}

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

class TerrainState implements ActiveTerrain {
  readonly terrainId: string;
  readonly config: TerrainConfig;
  hp: number;
  remainingMs: number;
  private _dead = false;

  constructor(terrainId: string, config: TerrainConfig) {
    this.terrainId = terrainId;
    this.config = config;
    this.hp = config.hp;
    this.remainingMs = config.durationMs;
  }

  get isDead(): boolean {
    return this._dead;
  }

  takeDamage(amount: number): boolean {
    if (this._dead) return false;
    this.hp = Math.max(0, this.hp - amount);
    if (this.hp === 0) {
      this._dead = true;
      return true;
    }
    return false;
  }

  kill(): void {
    this.hp = 0;
    this._dead = true;
  }
}

// ---------------------------------------------------------------------------
// System
// ---------------------------------------------------------------------------

export class TerrainConstructionSystem {
  private readonly terrain = new Map<string, TerrainState>();
  private readonly events: TerrainEvent[] = [];
  private nextId = 1;

  /** Place a terrain object. Returns instance ID. */
  place(config: TerrainConfig): string {
    const terrainId = `terrain_${this.nextId++}`;
    const state = new TerrainState(terrainId, config);
    this.terrain.set(terrainId, state);

    this.events.push({
      type: 'placed',
      terrainId,
      config,
      x: config.position.x,
      y: config.position.y,
    });

    return terrainId;
  }

  /** Remove a terrain object. Triggers onDestroyEffect if present. */
  remove(terrainId: string): void {
    const state = this.terrain.get(terrainId);
    if (!state) return;
    state.kill();

    this.events.push({
      type: 'destroyed',
      terrainId,
      config: state.config,
      x: state.config.position.x,
      y: state.config.position.y,
      destroyEffect: state.config.onDestroyEffect,
    });

    this.terrain.delete(terrainId);
  }

  /** Get count of active terrain, optionally filtered by type. */
  getActiveCount(type?: TerrainType): number {
    if (type === undefined) {
      return Array.from(this.terrain.values()).filter(t => !t.isDead).length;
    }
    return Array.from(this.terrain.values())
      .filter(t => !t.isDead && t.config.type === type).length;
  }

  /** Get a terrain instance by ID. */
  get(terrainId: string): ActiveTerrain | undefined {
    return this.terrain.get(terrainId);
  }

  /** Get all active terrain instances. */
  getAll(): readonly ActiveTerrain[] {
    return Array.from(this.terrain.values()).filter(t => !t.isDead);
  }

  /** Count active terrain of a given type within a radius (in tiles) of a point. */
  getActiveCountInRadius(type: string, x: number, y: number, radiusTiles: number, tileSizePx: number): number {
    const radiusPx = radiusTiles * tileSizePx;
    const radiusSq = radiusPx * radiusPx;
    let count = 0;
    for (const state of this.terrain.values()) {
      if (state.isDead || state.config.type !== type) continue;
      const dx = state.config.position.x - x;
      const dy = state.config.position.y - y;
      if (dx * dx + dy * dy <= radiusSq) count++;
    }
    return count;
  }

  /** Get all active formations within a radius (in tiles) of a point. */
  getFormationsInRadius(x: number, y: number, radiusTiles: number, tileSizePx: number): Array<{ id: string; position: { x: number; y: number } }> {
    const radiusPx = radiusTiles * tileSizePx;
    const radiusSq = radiusPx * radiusPx;
    const results: Array<{ id: string; position: { x: number; y: number } }> = [];
    for (const state of this.terrain.values()) {
      if (state.isDead || state.config.type !== 'formation') continue;
      const dx = state.config.position.x - x;
      const dy = state.config.position.y - y;
      if (dx * dx + dy * dy <= radiusSq) {
        results.push({ id: state.terrainId, position: { ...state.config.position } });
      }
    }
    return results;
  }

  /** Damage a terrain object. Returns true if destroyed. */
  damage(terrainId: string, amount: number): boolean {
    const state = this.terrain.get(terrainId);
    if (!state || state.isDead) return false;
    if (state.config.indestructible) return false;

    const died = state.takeDamage(amount);

    this.events.push({
      type: 'damaged',
      terrainId,
      config: state.config,
      x: state.config.position.x,
      y: state.config.position.y,
    });

    if (died) {
      this.events.push({
        type: 'destroyed',
        terrainId,
        config: state.config,
        x: state.config.position.x,
        y: state.config.position.y,
        destroyEffect: state.config.onDestroyEffect,
      });
      this.terrain.delete(terrainId);
    }

    return died;
  }

  /**
   * Per-frame update: tick durations, sweep expired terrain.
   * Returns events generated this frame.
   */
  update(delta: number): TerrainEvent[] {
    this.events.length = 0;
    const expired: string[] = [];

    for (const [id, state] of this.terrain) {
      if (state.isDead) continue;

      // Duration expiry (0 = permanent)
      if (state.config.durationMs > 0) {
        state.remainingMs -= delta;
        if (state.remainingMs <= 0) {
          state.kill();
          expired.push(id);
          this.events.push({
            type: 'expired',
            terrainId: id,
            config: state.config,
            x: state.config.position.x,
            y: state.config.position.y,
          });
        }
      }
    }

    for (const id of expired) {
      this.terrain.delete(id);
    }

    return [...this.events];
  }

  /** Drain events generated outside of update (from place/remove/damage). */
  drainEvents(): TerrainEvent[] {
    const drained = [...this.events];
    this.events.length = 0;
    return drained;
  }

  /** Clear all terrain state. Called on scene shutdown to prevent leaks across run restarts. */
  clear(): void {
    this.terrain.clear();
    this.events.length = 0;
    this.nextId = 1;
  }
}
